import { supabase } from "../lib/supabase";

export type NovaCmsNotificationType =
  | "reservation"
  | "service_enquiry"
  | "feedback"
  | "website_enquiry"
  | "order"
  | "system";

export type NovaCmsNotification = {
  id: string;
  notification_type: NovaCmsNotificationType;
  title: string;
  message: string;
  destination: string | null;
  entity_type: string | null;
  entity_id: string | null;
  is_read: boolean;
  created_at: string;
};

type NovaCmsNotificationRow = {
  id: string;
  notification_type: string;
  title: string;
  message: string;
  destination: string | null;
  entity_type: string | null;
  entity_id: string | null;
  is_read: boolean;
  created_at: string;
};

const NOTIFICATION_TYPES =
  new Set<NovaCmsNotificationType>([
    "reservation",
    "service_enquiry",
    "feedback",
    "website_enquiry",
    "order",
    "system",
  ]);

function isNovaCmsNotificationType(
  value: string,
): value is NovaCmsNotificationType {
  return NOTIFICATION_TYPES.has(
    value as NovaCmsNotificationType,
  );
}

function getNotificationTitle(
  row: NovaCmsNotificationRow,
): string {
  if (row.notification_type !== "service_enquiry") {
    return row.title;
  }

  const match = row.message.match(
    /submitted an? (.+?) enquiry\.?$/i,
  );

  if (!match?.[1]) {
    return row.title;
  }

  return `New ${match[1].trim()} enquiry`;
}

function normalizeNotification(
  row: NovaCmsNotificationRow,
): NovaCmsNotification | null {
  if (
    !isNovaCmsNotificationType(
      row.notification_type,
    )
  ) {
    console.warn(
      "[Nova CMS] unsupported notification type",
      row.notification_type,
    );

    return null;
  }

  return {
    id: row.id,
    notification_type:
      row.notification_type,
    title: getNotificationTitle(row),
    message: row.message,
    destination: row.destination,
    entity_type: row.entity_type,
    entity_id: row.entity_id,
    is_read: row.is_read,
    created_at: row.created_at,
  };
}

export async function loadNovaCmsNotifications(
  limit = 20,
): Promise<NovaCmsNotification[]> {
  const { data, error } = await supabase
    .from("nova_cms_notifications")
    .select(
      "id,notification_type,title,message,destination,entity_type,entity_id,is_read,created_at",
    )
    .order("created_at", {
      ascending: false,
    })
    .limit(limit);

  if (error) {
    console.error(
      "[Nova CMS] notification load failed",
      error,
    );

    return [];
  }

  return (
    (data ?? []) as NovaCmsNotificationRow[]
  )
    .map(normalizeNotification)
    .filter(
      (
        notification,
      ): notification is NovaCmsNotification =>
        notification !== null,
    );
}

export async function markNovaCmsNotificationRead(
  id: string,
): Promise<boolean> {
  const { error } = await supabase
    .from("nova_cms_notifications")
    .update({
      is_read: true,
      read_at: new Date().toISOString(),
    })
    .eq("id", id);

  if (error) {
    console.error(
      "[Nova CMS] notification update failed",
      error,
    );

    return false;
  }

  return true;
}

export async function markAllNovaCmsNotificationsRead(): Promise<boolean> {
  const { error } = await supabase
    .from("nova_cms_notifications")
    .update({
      is_read: true,
      read_at: new Date().toISOString(),
    })
    .eq("is_read", false);

  if (error) {
    console.error(
      "[Nova CMS] mark-all-read failed",
      error,
    );

    return false;
  }

  return true;
}
